import React, { useEffect, useState } from "react";
import { Download, X, Share as ShareIcon, PlusSquare } from "lucide-react";

/**
 * PWAInstallPrompt
 * - On Android / Chrome / Edge: listens for `beforeinstallprompt`, shows a
 *   bottom-anchored install banner with a "Install app" button that triggers
 *   the native prompt.
 * - On iOS Safari (which doesn't fire the event): shows a manual instruction
 *   banner with the Share → Add to Home Screen icons.
 * - Dismissible; remembers "not now" for 7 days via localStorage.
 * - Hides itself once the app is running in standalone mode.
 */
const DISMISS_KEY = "mcl_pwa_dismiss_until";
const DISMISS_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until > Date.now();
  } catch {
    return false;
  }
}

function dismissFor(days) {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + days * 24 * 60 * 60 * 1000)
    );
  } catch {}
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("android"); // "android" | "ios"

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
      // Delay a moment so it doesn't flash right on load
      setTimeout(() => setVisible(true), 1500);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS manual prompt after a short delay if not already shown natively
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        setMode("ios");
        setVisible(true);
      }, 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  if (!visible) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
      } else {
        dismissFor(DISMISS_DAYS);
        setVisible(false);
      }
    } catch {
      dismissFor(DISMISS_DAYS);
      setVisible(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    dismissFor(DISMISS_DAYS);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Install Mabuhay Capital Lending app"
      data-testid="pwa-install-prompt"
      className="fixed z-[9998] inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
    >
      <div
        className="relative rounded-2xl shadow-2xl border border-amber-400/30 text-white overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0A1F44 0%, #12326b 55%, #0A1F44 100%)",
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full text-white/70 hover:text-white hover:bg-white/10"
          aria-label="Dismiss install prompt"
          data-testid="pwa-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <img
              src="/brand/logo-mark.png"
              alt=""
              className="h-12 w-12 rounded-lg shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>
                Install Mabuhay Capital Lending
              </div>
              <div className="mt-0.5 text-xs text-blue-100/85">
                {mode === "ios"
                  ? "Add to your home screen for a faster, app-like experience."
                  : "Get one-tap access with our native-like app."}
              </div>
            </div>
          </div>

          {mode === "android" ? (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleInstall}
                data-testid="pwa-install-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-amber-400 text-[#0A1F44] hover:bg-amber-300 transition-colors"
              >
                <Download className="h-4 w-4" /> Install app
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="px-3 py-2 rounded-lg text-sm text-white/85 hover:bg-white/10"
              >
                Not now
              </button>
            </div>
          ) : (
            <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-3 text-xs text-blue-100/90 leading-relaxed">
              <div className="flex items-center gap-1.5">
                <span>Tap</span>
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-white/10 border border-white/15">
                  <ShareIcon className="h-3.5 w-3.5" />
                </span>
                <span>then</span>
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-white/10 border border-white/15">
                  <PlusSquare className="h-3.5 w-3.5" />
                </span>
                <span>“Add to Home Screen”.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
